import { Add, Delete } from '@mui/icons-material';
import { Autocomplete, Box, Button, IconButton, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import MoneyInputWithBlur from '../MoneyInputWithBlur';
import { formatCurrencyInCents, parseFormattedMoneyToCents } from '../../helpers/formatters';
import { emptyPartRow } from '../../helpers/printerMaintenances';
import api from '../../api';

function centsToReaisInput(cents) {
    const n = Number(cents) || 0;
    return n ? n / 100 : '';
}

function stockOptionLabel(option) {
    if (!option) {
        return '';
    }
    if (typeof option === 'string') {
        return option;
    }
    return option.name || '';
}

export default function ReplacementPartsFields({ value, onChange, printer }) {
    const rows = Array.isArray(value) && value.length ? value : [emptyPartRow()];
    const [stockParts, setStockParts] = useState([]);
    const printerBrand = String(printer?.brand || '').trim();
    const printerModel = String(printer?.model || '').trim();
    const canSearchStock = Boolean(printer?.id && printerBrand);

    useEffect(() => {
        if (!canSearchStock) {
            setStockParts([]);
            return undefined;
        }

        let cancelled = false;
        api.get('replacement-parts', {
            params: {
                page: 1,
                limit: 100,
                printer_brand: printerBrand,
                printer_model: printerModel,
                in_stock: 1,
            },
        })
            .then((response) => {
                if (cancelled) {
                    return;
                }
                setStockParts(response.data?.data?.items || []);
            })
            .catch(() => {
                if (!cancelled) {
                    setStockParts([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [canSearchStock, printerBrand, printerModel]);

    const patchRow = (index, patch) => {
        onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const addRow = () => onChange([...rows, emptyPartRow()]);

    const removeRow = (index) => {
        onChange(rows.length <= 1 ? [emptyPartRow()] : rows.filter((_, i) => i !== index));
    };

    return (
        <>
            <Typography variant="subtitle1" gutterBottom>
                Troca de peça
            </Typography>
            {rows.map((part, index) => (
                <PartRow
                    key={index}
                    index={index}
                    part={part}
                    stockParts={stockParts}
                    canSearchStock={canSearchStock}
                    onPatch={(patch) => patchRow(index, patch)}
                    onRemove={() => removeRow(index)}
                />
            ))}
            <Button startIcon={<Add />} onClick={addRow} type="button">
                Adicionar peça
            </Button>
        </>
    );
}

function PartRow({ index, part, stockParts, canSearchStock, onPatch, onRemove }) {
    const options = useMemo(() => {
        const list = [...stockParts];
        if (part.stock_part_uuid && !list.some((item) => item.id === part.stock_part_uuid)) {
            list.unshift({
                id: part.stock_part_uuid,
                name: part.name,
                unit_value_cents: part.value_cents,
                quantity: 0,
            });
        }
        return list;
    }, [stockParts, part.stock_part_uuid, part.name, part.value_cents]);

    const selectedOption = options.find((item) => item.id === part.stock_part_uuid) || null;

    return (
        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
            <Autocomplete
                freeSolo
                fullWidth
                options={options}
                value={selectedOption}
                inputValue={part.name || ''}
                getOptionLabel={stockOptionLabel}
                isOptionEqualToValue={(option, value) => option?.id && value?.id && option.id === value.id}
                filterOptions={(opts, state) => {
                    const query = String(state.inputValue || '').trim().toLowerCase();
                    if (!query) {
                        return opts;
                    }
                    return opts.filter((item) => String(item.name || '').toLowerCase().includes(query));
                }}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        {option.name}
                        {option.quantity != null ? ` · ${option.quantity} un.` : ''}
                        {option.unit_value_cents ? ` · ${formatCurrencyInCents(option.unit_value_cents)}` : ''}
                    </li>
                )}
                onChange={(_, newValue) => {
                    if (newValue && typeof newValue === 'object') {
                        onPatch({
                            name: newValue.name || '',
                            value_cents: Number(newValue.unit_value_cents) || 0,
                            stock_part_uuid: newValue.id || '',
                        });
                        return;
                    }
                    if (typeof newValue === 'string') {
                        onPatch({ name: newValue, stock_part_uuid: '' });
                        return;
                    }
                    onPatch({ name: '', stock_part_uuid: '' });
                }}
                onInputChange={(_, inputValue, reason) => {
                    if (reason !== 'input') {
                        return;
                    }
                    const selectedName = selectedOption?.name || '';
                    if (part.stock_part_uuid && inputValue === selectedName) {
                        onPatch({ name: inputValue });
                        return;
                    }
                    onPatch({ name: inputValue, stock_part_uuid: '' });
                }}
                noOptionsText={canSearchStock
                    ? 'Nenhuma peça em estoque para esta impressora'
                    : 'Selecione a impressora para buscar peças'}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={`Peça ${index + 1}`}
                        helperText={canSearchStock
                            ? 'Busque uma peça em estoque ou digite o nome livremente.'
                            : 'Selecione a impressora para buscar peças em estoque.'}
                    />
                )}
            />
            <Box sx={{ width: { xs: 140, sm: 180 }, flexShrink: 0 }}>
                <MoneyInputWithBlur
                    label="Valor"
                    value={centsToReaisInput(part.value_cents)}
                    onChange={(values) => onPatch({
                        value_cents: parseFormattedMoneyToCents(values?.floatValue, values?.value),
                    })}
                />
            </Box>
            <IconButton color="error" onClick={onRemove} aria-label="remover peça" sx={{ mt: 0.5 }}>
                <Delete />
            </IconButton>
        </Box>
    );
}
